import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { MeasurementComponent } from "../measurements-component";
import { MeasurementStartControlComponent } from "./measurement-start-control-component";
import { MeasurementEndControlComponent } from "./measurement-end-control-component";
import { MeasurementTextComponent } from "./measurement-text-component";

export class MeasurementSelectionComponent extends Webgl2Component {
  private _mouseDownPosition = Vec2.zero();
  private _prevStartPosition = Vec2.zero();
  private _prevEndPosition = Vec2.zero();

  private get _selectedComponent(): Nullable<MeasurementComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof MeasurementComponent ? component : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    const startControlComponent = new MeasurementStartControlComponent(this._renderer, this);
    startControlComponent.style.top = "50%";
    startControlComponent.style.translateY = "-50%";
    startControlComponent.style.left = "0";
    startControlComponent.style.translateX = "-50%";
    this.appendChild(startControlComponent);

    const endControlComponent = new MeasurementEndControlComponent(this._renderer, this);
    endControlComponent.style.top = "50%";
    endControlComponent.style.translateY = "-50%";
    endControlComponent.style.left = "100%";
    endControlComponent.style.translateX = "-50%";
    this.appendChild(endControlComponent);

    const textComponent = new MeasurementTextComponent(this._renderer);
    this.appendChild(textComponent);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    this._mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e);
    this._prevStartPosition = this._selectedComponent.startPos.clone();
    this._prevEndPosition = this._selectedComponent.endPos.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    const mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e);
    const diff = mouseDownPosition.sub(this._mouseDownPosition);
    this._selectedComponent?.changePositions(
      this._prevStartPosition.clone().add(diff),
      this._prevEndPosition.clone().add(diff),
    );
    this._renderer.render();
  }

  public getRenderData() {
    if (
      !this._selectedComponent ||
      (this._selectedComponent.startPos.isZero() && this._selectedComponent.endPos.isZero())
    ) {
      return;
    }

    this.transform.copy(this._selectedComponent.transform);

    const material = Webgl2.materials.backgroundColor;
    material.setUniforms({
      u_color: [0, 0.6, 0.63, 1],
    });
    return { material };
  }
}

export default MeasurementSelectionComponent;
